import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Car, Home, Truck, Sofa } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const typeIcons = {
  vehicles: Car,
  real_estate: Home,
  rvs: Truck,
  furniture: Sofa,
};

const typeColors = {
  vehicles: "#EF4444",
  real_estate: "#EC4899",
  rvs: "#06B6D4",
  furniture: "#8B5CF6",
};

export function CompareBar({ items, listingType, onRemove, onClear }) {
  const [isExpanded, setIsExpanded] = useState(true);
  
  const Icon = typeIcons[listingType] || Car;
  const color = typeColors[listingType] || "#7C3AED";
  
  if (items.length === 0) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-40 bg-[#0F172A] border-t border-white/10 shadow-2xl"
      >
        {/* Toggle Bar */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="absolute -top-10 left-1/2 -translate-x-1/2 px-4 py-2 bg-[#0F172A] border border-white/10 border-b-0 rounded-t-lg flex items-center gap-2 text-white text-sm"
          style={{ boxShadow: `0 -4px 20px ${color}30` }}
        >
          <Icon className="w-4 h-4" style={{ color }} />
          Compare ({items.length}/4)
        </button>
        
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              className="overflow-hidden"
            >
              <div className="max-w-7xl mx-auto px-4 py-4">
                <div className="flex items-center gap-4">
                  {/* Selected Items */}
                  <div className="flex-1 flex items-center gap-3 overflow-x-auto pb-2">
                    {items.map((item, idx) => (
                      <motion.div
                        key={item.id}
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        exit={{ scale: 0, opacity: 0 }}
                        className="relative flex-shrink-0 w-40 bg-white/5 rounded-lg overflow-hidden border border-white/10"
                      >
                        {/* Remove button */}
                        <button
                          onClick={() => onRemove(item.id)}
                          className="absolute top-1 right-1 z-10 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center hover:bg-red-500 transition-colors"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                        
                        {/* Image */}
                        <div className="aspect-[16/10] bg-white/10">
                          {item.images?.[0] || item.thumbnail_url ? (
                            <img
                              src={item.images?.[0] || item.thumbnail_url}
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Icon className="w-8 h-8 text-[#64748B]" />
                            </div>
                          )}
                        </div>
                        
                        {/* Info */}
                        <div className="p-2">
                          <p className="text-white text-sm font-medium truncate">
                            {listingType === "vehicles" 
                              ? `${item.year} ${item.make}` 
                              : listingType === "real_estate"
                              ? `${item.bedrooms || "?"} bd / ${item.bathrooms || "?"} ba`
                              : item.title?.substring(0, 20)
                            }
                          </p>
                          <p className="text-xs" style={{ color }}>
                            {item.price ? `$${item.price.toLocaleString()}` : "Call"}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                    
                    {/* Empty slots */}
                    {Array.from({ length: 4 - items.length }).map((_, idx) => (
                      <div
                        key={`empty-${idx}`}
                        className="flex-shrink-0 w-40 h-[120px] bg-white/5 rounded-lg border border-dashed border-white/20 flex items-center justify-center"
                      >
                        <span className="text-[#64748B] text-xs">Select to compare</span>
                      </div>
                    ))}
                  </div>
                  
                  {/* Actions */}
                  <div className="flex flex-col gap-2">
                    <Link to={`/compare/${listingType}?ids=${items.map(i => i.id).join(",")}`}>
                      <Button 
                        className="whitespace-nowrap"
                        style={{ backgroundColor: color }}
                        disabled={items.length < 2}
                      >
                        Compare {items.length > 1 ? `(${items.length})` : ""}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </Link>
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={onClear}
                      className="text-[#94A3B8] hover:text-white"
                    >
                      Clear All
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </AnimatePresence>
  );
}

export default CompareBar;
